import { LOCATION_STATUSES, LOCATION_TYPES } from '../../constants/warehouseLocations';
import { FormatBrl, FormatInt } from '../../utils/format';

/**
 * Helpers compartilhados dos formulários/tabela de Localizações (M1):
 * parse de INPUT do usuário (pt-BR, vírgula decimal) e display de medidas.
 * Formatação de exibição compõe os formatadores canônicos de `utils/format`.
 *
 * `ParseDecimalInput`/`ParseIntegerInput` foram promovidos para `utils/format`
 * (o cadastro de produto usa o mesmo parse); ficam reexportados aqui para os
 * importadores do armazém não mudarem.
 */
export { ParseDecimalInput, ParseIntegerInput } from '../../utils/format';

// Teto de segurança do parser de lista: uma faixa digitada errada ("1-99999")
// não pode congelar a aba. O galpão real tem dezenas de blocos/andares.
const NUMBER_LIST_MAX_SPAN = 500;

/**
 * Lista multi do bulk v2 ("1,3,5-9") → number[] único e ordenado.
 * '' → null (= aplicar a todos); inválido → NaN (o submit acusa).
 * Aceita números (>=1) e faixas "a-b" separados por vírgula.
 */
export function ParseNumberListInput(raw) {
    const text = String(raw ?? '').trim();
    if (!text) {
        return null;
    }
    const values = new Set();
    for (const part of text.split(',')) {
        const piece = part.trim();
        if (!piece) {
            return Number.NaN;
        }
        const range = piece.match(/^(\d+)\s*-\s*(\d+)$/);
        if (range) {
            const start = Number(range[1]);
            const end = Number(range[2]);
            if (start < 1 || end < start || end - start > NUMBER_LIST_MAX_SPAN) {
                return Number.NaN;
            }
            for (let value = start; value <= end; value += 1) {
                values.add(value);
            }
        } else if (/^\d+$/.test(piece)) {
            const value = Number(piece);
            if (value < 1) {
                return Number.NaN;
            }
            values.add(value);
        } else {
            return Number.NaN;
        }
    }
    return [...values].sort((a, b) => a - b);
}

/** Peso em kg para display: inteiro sem casas ("1.000"), fração com 2 ("980,50"). */
export function FormatWeightKg(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return null;
    }
    return Number.isInteger(number) ? FormatInt(number) : FormatBrl(number);
}

/** Largura de vão em metros para display, sempre 2 casas ("2,50"). */
export function FormatBayWidth(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? FormatBrl(number) : null;
}

/** Options de `type` para Select, rotuladas pelo dicionário. */
export function BuildLocationTypeOptions(label) {
    return LOCATION_TYPES.map((type) => ({ value: type, label: label(`wms.location_type.${type}`) }));
}

/** Options de `status` para Select, rotuladas pelo dicionário. */
export function BuildLocationStatusOptions(label) {
    return LOCATION_STATUSES.map((status) => ({ value: status, label: label(`wms.location_status.${status}`) }));
}

/**
 * Variante do Badge para o STATUS físico (r3: Ativa/Bloqueada/Manutenção).
 * BLOCKED segue warning (mesma família de cor do minimapa); MAINTENANCE usa
 * info para não se confundir com bloqueio.
 */
export function StatusBadgeVariant(status) {
    if (status === 'ACTIVE') {
        return 'success';
    }
    if (status === 'BLOCKED') {
        return 'warning';
    }
    return status === 'MAINTENANCE' ? 'info' : 'default';
}

/** Variante do Badge para a OCUPAÇÃO (Livre = cinza por ora — dado do M3/M5). */
export function OccupancyBadgeVariant(occupancy) {
    if (occupancy === 'OCCUPIED') {
        return 'info';
    }
    return occupancy === 'RESERVED' ? 'warning' : 'neutral';
}
